/**
 * Dates a task is due, as plain `YYYY-MM-DD` strings. A due date has no
 * time and no zone, so none of this goes through `Date` arithmetic on local
 * time: a day is added in UTC on the date's own parts.
 */

const ISO_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export const WEEKDAY_NAMES = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;
const WEEKDAY_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** Whether `value` is a real calendar day in `YYYY-MM-DD` form. */
export function isIsoDay(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  const m = ISO_DAY.exec(value);
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const date = new Date(Date.UTC(y, mo - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === mo - 1 && date.getUTCDate() === d;
}

function utc(iso: string): Date {
  const m = ISO_DAY.exec(iso);
  return new Date(Date.UTC(Number(m?.[1]), Number(m?.[2]) - 1, Number(m?.[3])));
}

function format(date: Date): string {
  return date.toISOString().slice(0, 10);
}

/** The viewer's own calendar day. */
export function todayIso(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function addDays(iso: string, days: number): string {
  const date = utc(iso);
  date.setUTCDate(date.getUTCDate() + days);
  return format(date);
}

/** 0 is Sunday. */
export function weekdayOf(iso: string): number {
  return utc(iso).getUTCDay();
}

/** The Sunday that closes the week `iso` falls in. A week runs Monday to Sunday. */
function endOfWeek(iso: string): string {
  const day = weekdayOf(iso);
  return addDays(iso, day === 0 ? 0 : 7 - day);
}

export type DayBucket = 'overdue' | 'today' | 'week' | 'later' | 'none';

export const BUCKET_LABELS: Record<DayBucket, string> = {
  overdue: 'Overdue',
  today: 'Today',
  week: 'This week',
  later: 'Later',
  none: 'No date',
};

export function bucketOf(dueOn: string | null, today: string): DayBucket {
  if (!dueOn) return 'none';
  if (dueOn < today) return 'overdue';
  if (dueOn === today) return 'today';
  if (dueOn <= endOfWeek(today)) return 'week';
  return 'later';
}

/** How a row says when: "Today", "Tomorrow", a weekday this week, else "Oct 9". */
export function dueLabel(dueOn: string, today: string): string {
  if (dueOn === today) return 'Today';
  if (dueOn === addDays(today, 1)) return 'Tomorrow';
  if (dueOn > today && dueOn <= endOfWeek(today)) return WEEKDAY_SHORT[weekdayOf(dueOn)] ?? dueOn;
  return shortDay(dueOn);
}

/** "Oct 9". */
export function shortDay(iso: string): string {
  const date = utc(iso);
  return `${MONTH_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`;
}

/** "Fri 25 Sept" -- the long form the task page and the make-a-task form use. */
export function longDay(iso: string): string {
  const date = utc(iso);
  const month = MONTH_SHORT[date.getUTCMonth()] ?? '';
  return `${WEEKDAY_SHORT[date.getUTCDay()]} ${date.getUTCDate()} ${month === 'Sep' ? 'Sept' : month}`;
}

const WHEN_PHRASE = new RegExp(
  `\\b((?:before|by|on|until)\\s+(?:end of (?:the )?day\\s+)?)?(tomorrow|today|${WEEKDAY_NAMES.join('|')})\\b`,
  'i',
);

/**
 * A due date read out of words: "before Friday", "by end of day tomorrow",
 * "Call the bank Friday". A weekday means its next occurrence after today.
 * `phrase` is the words it was read from, so the form can say so, and
 * `start` where they begin in `text`.
 */
export function suggestDue(
  text: string,
  today: string,
): { dueOn: string; phrase: string; start: number } | null {
  const match = WHEN_PHRASE.exec(text);
  const word = match?.[2]?.toLowerCase();
  if (!match || !word) return null;
  let dueOn: string;
  if (word === 'today') dueOn = today;
  else if (word === 'tomorrow') dueOn = addDays(today, 1);
  else {
    const target = WEEKDAY_NAMES.indexOf(word as (typeof WEEKDAY_NAMES)[number]);
    const ahead = ((target - weekdayOf(today) + 6) % 7) + 1;
    dueOn = addDays(today, ahead);
  }
  return { dueOn, phrase: match[0], start: match.index };
}
